import { useRef, useState, useEffect } from 'react';
import {
  Type, ImagePlus, Trash2, Bold, Italic, Underline, Strikethrough,
  AlignLeft, AlignCenter, AlignRight, Undo2, Redo2, X, Calendar, PenTool,
  Highlighter, Pencil, Square, Circle, Minus, Eraser, ChevronDown,
  Check, XIcon, ArrowUpRight, AlignJustify, MousePointer2, FileSearch,
} from 'lucide-react';
import ColorPicker from './ColorPicker';
import TextFormatPopover from './TextFormatPopover';
import SaveMenu from './SaveMenu';
import SignatureMenu from './SignatureMenu';
import { isDesktop } from '@/lib/fileSystem';
import { hasFeature } from '@/lib/license';
import { ProLock } from './ProBadge';

const FONTS = [
  { id: 'Arial', label: 'Arial' },
  { id: 'Helvetica', label: 'Helvetica' },
  { id: 'Times-Roman', label: 'Times New Roman' },
  { id: 'Courier', label: 'Courier' },
  { id: 'Lato', label: 'Lato' },
  { id: 'Roboto', label: 'Roboto' },
  { id: 'Roboto Slab', label: 'Roboto Slab' },
  { id: 'Inter', label: 'Inter' },
  { id: 'Carlito', label: 'Calibri' },
];

const FONT_SIZES = [8, 10, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48];

const LINE_HEIGHTS = [
  { value: 1.0, label: 'Tight' },
  { value: 1.3, label: 'Normal' },
  { value: 1.6, label: 'Relaxed' },
  { value: 2.0, label: 'Double' },
];

// Dropdown tool groups
const PEN_TOOLS = [
  { id: 'draw', icon: Pencil, label: 'Pencil', lite: true },
  { id: 'highlight-draw', icon: Highlighter, label: 'Highlighter', lite: true },
  { id: 'eraser', icon: Eraser, label: 'Eraser', lite: true },
];

const SHAPE_TOOLS = [
  { id: 'rectangle', icon: Square, label: 'Rectangle' },
  { id: 'ellipse', icon: Circle, label: 'Circle' },
  { id: 'checkmark', icon: Check, label: 'Checkmark' },
  { id: 'cross', icon: XIcon, label: 'Cross' },
  { id: 'line', icon: Minus, label: 'Line' },
  { id: 'arrow', icon: ArrowUpRight, label: 'Arrow' },
];

/**
 * EditToolbar — Unified toolbar for edit mode.
 *
 * Row 1: Edit tools (Text, Image, Sign, Date) | Dropdown groups (Pen, Shapes) | Delete + Undo/Redo + Save + Close
 * Row 2: Contextual options based on active tool or selection
 */
export default function EditToolbar({
  editTool,
  selectedEditIds,
  selectedEditType,
  editStyles,
  canUndo,
  canRedo,
  hasUnsavedChanges,
  onSetTool,
  onUpdateStyle,
  onDeleteSelected,
  onAddImage,
  onUndo,
  onRedo,
  onSave,
  onSaveAs,
  onDownload,
  onSaveToCloud,
  onPrint,
  onFlatten,
  hasOverlays,
  processing,
  docSource,
  cloudState,
  cloudUpsell,
  onClose,
  selectedEditRect,
  signatureDataUrl,
  onOpenSignaturePanel,
  // Saved signatures (lifted from Viewer so the modal + menu share state)
  savedSigs,
  onSelectSavedSignature,
  onDeleteSavedSignature,
  // Annotation props
  annotActiveTool,
  annotColor,
  annotOpacity,
  annotStrokeWidth,
  annotCanUndo,
  annotCanRedo,
  onSelectAnnotTool,
  onSetAnnotColor,
  onSetAnnotOpacity,
  onSetAnnotStrokeWidth,
  onAnnotUndo,
  onAnnotRedo,
  onDeselectAll,
  // Selected annotation
  selectedAnnotation,
  onDeleteSelectedAnnotation,
  // Selection-aware formatting
  selectionStyles,        // { fontFamily, fontSize, bold, italic, ... } from current selection
  onUpdateSegmentStyle,   // (key, value) => void — applies to selected text range
  // Content detection
  hasDetectedContent,     // boolean — whether Edit Text detection has been run
}) {
  const imgInputRef = useRef(null);
  const hasSelection = selectedEditIds && selectedEditIds.size > 0;
  const showTextOptions = (hasSelection && selectedEditType === 'text') || editTool === 'text' || editTool === 'date';
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const [openDropdown, setOpenDropdown] = useState(null); // 'pen' | 'shapes' | null
  const dropdownRef = useRef(null);

  // Contextual control flags — based on active tool OR selected annotation
  const activeOrSelected = annotActiveTool || selectedAnnotation;
  const effectiveType = annotActiveTool || selectedAnnotation?.type;
  const showAnnotColors = activeOrSelected && effectiveType !== 'eraser';
  const showAnnotStroke = ['draw', 'highlight-draw', 'rectangle', 'ellipse', 'line', 'arrow', 'checkmark', 'cross'].includes(effectiveType);
  const showAnnotOpacity = activeOrSelected && effectiveType !== 'eraser';
  const showEraserThickness = annotActiveTool === 'eraser';

  // Which group is the current annotation tool in?
  const penToolIds = PEN_TOOLS.map((t) => t.id);
  const shapeToolIds = SHAPE_TOOLS.map((t) => t.id);
  const isPenActive = penToolIds.includes(annotActiveTool);
  const isShapeActive = shapeToolIds.includes(annotActiveTool);

  // When selecting an annotation tool, clear the edit tool (and vice versa)
  const handleEditToolClick = (tool) => {
    if (onSelectAnnotTool) onSelectAnnotTool(null);
    onSetTool(tool);
    setOpenDropdown(null);
  };

  const handleAnnotToolClick = (toolId) => {
    onSetTool('select');
    if (onDeselectAll) onDeselectAll(); // deselect text edits so Row 2 switches to annotation options
    if (onSelectAnnotTool) onSelectAnnotTool(toolId);
    setOpenDropdown(null);
  };

  // Dropdown: always open on click (no auto-select)
  const handleGroupClick = (group) => {
    setOpenDropdown((prev) => (prev === group ? null : group));
  };

  const handleSubToolSelect = (group, toolId) => {
    handleAnnotToolClick(toolId);
  };

  // Close dropdown on outside click
  useEffect(() => {
    if (!openDropdown) return;
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    };
    window.addEventListener('mousedown', handleClick);
    return () => window.removeEventListener('mousedown', handleClick);
  }, [openDropdown]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Combined undo/redo
  const combinedCanUndo = canUndo || annotCanUndo;
  const combinedCanRedo = canRedo || annotCanRedo;
  const handleUndo = () => {
    if (annotActiveTool && annotCanUndo) onAnnotUndo?.();
    else if (canUndo) onUndo();
    else if (annotCanUndo) onAnnotUndo?.();
  };
  const handleRedo = () => {
    if (annotActiveTool && annotCanRedo) onAnnotRedo?.();
    else if (canRedo) onRedo();
    else if (annotCanRedo) onAnnotRedo?.();
  };

  const showContextRow = showTextOptions || !!annotActiveTool || !!selectedAnnotation;

  return (
    <>
      <div className="bg-white border-b border-gray-200 px-4 py-2 shrink-0" ref={dropdownRef}>
        {/* Row 1: Tool buttons */}
        <div className="flex items-center gap-1 flex-wrap">
          {/* Select tool — default cursor mode */}
          <ToolBtn active={editTool === 'select' && !annotActiveTool} onClick={() => handleEditToolClick('select')} icon={MousePointer2} label="Select" />

          <div className="w-px h-6 bg-gray-300 mx-1" />

          {/* Edit insert tools */}
          <ToolBtn active={editTool === 'text'} onClick={() => handleEditToolClick('text')} icon={Type} label="Text" />
          <button
            onClick={() => {
              // Route through handleEditToolClick so this genuinely becomes
              // the active edit tool (clears any annotation tool, matches the
              // `editTool === 'image'` highlight below) BEFORE opening the
              // file picker. This is the sanctioned user action that gates
              // the whole-document image sweep in Viewer.jsx — merely
              // opening Edit Text must never trigger it, but choosing the
              // image tool is a deliberate click.
              handleEditToolClick('image');
              imgInputRef.current?.click();
            }}
            className={`flex items-center justify-center gap-1 px-2 py-1.5 rounded-md text-sm transition-colors min-h-[44px] sm:min-h-0 ${
              editTool === 'image' ? 'bg-brand-blue text-white' : 'text-gray-600 hover:bg-gray-100'
            }`}
            title="Add Image"
            aria-label="Add Image"
          >
            <ImagePlus size={15} />
            <span className="text-xs hidden sm:inline">Add image</span>
          </button>
          <ToolBtn active={editTool === 'image'} onClick={() => handleEditToolClick('image')} icon={ImagePlus} label="Edit images" />
          <input ref={imgInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/svg+xml" onChange={(e) => { onAddImage(e.target.files[0]); e.target.value = ''; }} className="hidden" />
          {savedSigs?.signedIn ? (
            <SignatureMenu
              active={editTool === 'signature'}
              signatures={savedSigs.signatures}
              loading={savedSigs.loading}
              plan={savedSigs.plan}
              cap={savedSigs.cap}
              used={savedSigs.used}
              capReached={savedSigs.capReached}
              onSelect={(sig) => {
                if (onSelectAnnotTool) onSelectAnnotTool(null);
                onSelectSavedSignature?.(sig);
              }}
              onDelete={(id) => onDeleteSavedSignature?.(id)}
              onAddNew={() => {
                if (onSelectAnnotTool) onSelectAnnotTool(null);
                onOpenSignaturePanel();
              }}
            />
          ) : (
            <ToolBtn
              active={editTool === 'signature'}
              onClick={() => {
                if (onSelectAnnotTool) onSelectAnnotTool(null);
                if (!signatureDataUrl) { onOpenSignaturePanel(); return; }
                onSetTool('signature');
              }}
              icon={PenTool}
              label="Sign"
            />
          )}
          {!savedSigs?.signedIn && signatureDataUrl && editTool === 'signature' && (
            <button
              onClick={onOpenSignaturePanel}
              className="text-xs text-brand-blue hover:underline -ml-1"
              title="Change signature"
            >
              Change
            </button>
          )}
          <ToolBtn active={editTool === 'date'} onClick={() => handleEditToolClick('date')} icon={Calendar} label="Date" />

          {/* Divider */}
          <div className="w-px h-6 bg-gray-300 mx-1" />

          {/* Dropdown groups — Pen and Shapes only (no Markup) */}
          <DropdownGroup
            label="Pen"
            tools={PEN_TOOLS}
            activeTool={annotActiveTool}
            isGroupActive={isPenActive}
            open={openDropdown === 'pen'}
            onToggle={() => handleGroupClick('pen')}
            onSelect={(id) => handleSubToolSelect('pen', id)}
          />
          <DropdownGroup
            label="Shapes"
            tools={SHAPE_TOOLS}
            activeTool={annotActiveTool}
            isGroupActive={isShapeActive}
            open={openDropdown === 'shapes'}
            onToggle={() => handleGroupClick('shapes')}
            onSelect={(id) => handleSubToolSelect('shapes', id)}
            locked={!hasFeature('annotations-full')}
          />

          {/* Divider + Edit Text (Pro) */}
          <div className="w-px h-6 bg-gray-300 mx-1" />
          <ToolBtn
            active={editTool === 'editText'}
            onClick={() => handleEditToolClick('editText')}
            icon={FileSearch}
            label={hasDetectedContent ? 'Edit Text ✓' : 'Edit Text'}
            locked={!hasFeature('edit-mode')}
            proBadge
          />

          {/* Delete */}
          {hasSelection && (
            <>
              <div className="w-px h-6 bg-gray-200 mx-1" />
              <button onClick={onDeleteSelected} className="p-1.5 rounded-md text-red-500 hover:bg-red-50 transition-colors" title="Delete selected (Del)" aria-label="Delete selected">
                <Trash2 size={15} />
              </button>
            </>
          )}

          {/* Delete selected annotation */}
          {!hasSelection && selectedAnnotation && (
            <>
              <div className="w-px h-6 bg-gray-200 mx-1" />
              <button onClick={onDeleteSelectedAnnotation} className="p-1.5 rounded-md text-red-500 hover:bg-red-50 transition-colors" title="Delete annotation (Del)" aria-label="Delete annotation">
                <Trash2 size={15} />
              </button>
            </>
          )}

          <div className="flex-1" />

          {/* Undo / Redo */}
          <button onClick={handleUndo} disabled={!combinedCanUndo} className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-30" title="Undo (Ctrl+Z)" aria-label="Undo">
            <Undo2 size={15} />
          </button>
          <button onClick={handleRedo} disabled={!combinedCanRedo} className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-30" title="Redo (Ctrl+Shift+Z)" aria-label="Redo">
            <Redo2 size={15} />
          </button>

          {/* Save / Download split button — label matches platform (desktop = Save, web = Download) */}
          {onSave && (
            <>
              <div className="w-px h-5 bg-gray-200 mx-1" />
              <SaveMenu
                platform={isDesktop() ? 'desktop' : 'web'}
                docSource={docSource || 'new'}
                cloudState={cloudState || 'signed-out'}
                cloudUpsell={cloudUpsell}
                hasUnsavedChanges={hasUnsavedChanges}
                onSave={onSave}
                onSaveAs={onSaveAs}
                onDownload={onDownload}
                onSaveToCloud={onSaveToCloud}
                onPrint={onPrint}
                onFlatten={onFlatten}
                hasOverlays={hasOverlays}
                disabled={processing}
              />
            </>
          )}

          {onClose && (
            <button onClick={onClose} className="p-1.5 rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600 ml-1" title="Close edit mode" aria-label="Close edit mode">
              <X size={15} />
            </button>
          )}
        </div>

        {/* Row 2: Contextual options — smooth transition to avoid layout shift */}
        {!isMobile && (
          <div className={`transition-all duration-150 ease-out ${
            showContextRow ? 'max-h-14 mt-2 pt-2 border-t border-gray-100 opacity-100 overflow-visible' : 'max-h-0 opacity-0 overflow-hidden'
          }`}>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Text formatting options */}
            {showTextOptions && (
              <>
                <ButtonDropdown
                  value={selectionStyles?.fontFamily || editStyles.fontFamily}
                  options={FONTS.map((f) => ({ value: f.id, label: f.label }))}
                  onChange={(val) => {
                    if (selectionStyles) onUpdateSegmentStyle?.('fontFamily', val);
                    else onUpdateStyle('fontFamily', val);
                  }}
                  renderOption={(opt) => <span style={{ fontFamily: opt.value }}>{opt.label}</span>}
                  className="h-7 min-w-[100px]"
                  title="Font family"
                />

                <ButtonDropdown
                  value={selectionStyles?.fontSize || editStyles.fontSize}
                  options={FONT_SIZES.map((s) => ({ value: s, label: String(s) }))}
                  onChange={(val) => {
                    const size = typeof val === 'string' ? parseInt(val, 10) : val;
                    if (selectionStyles) onUpdateSegmentStyle?.('fontSize', size);
                    else onUpdateStyle('fontSize', size);
                  }}
                  className="h-7 w-14"
                  title="Font size"
                />

                <StyleBtn active={selectionStyles?.bold ?? editStyles.bold} onClick={() => {
                  const newVal = !(selectionStyles?.bold ?? editStyles.bold);
                  if (selectionStyles) onUpdateSegmentStyle?.('bold', newVal);
                  else onUpdateStyle('bold', newVal);
                }} icon={Bold} title="Bold" />
                <StyleBtn active={selectionStyles?.italic ?? editStyles.italic} onClick={() => {
                  const newVal = !(selectionStyles?.italic ?? editStyles.italic);
                  if (selectionStyles) onUpdateSegmentStyle?.('italic', newVal);
                  else onUpdateStyle('italic', newVal);
                }} icon={Italic} title="Italic" />
                <StyleBtn active={selectionStyles?.underline ?? editStyles.underline} onClick={() => {
                  const newVal = !(selectionStyles?.underline ?? editStyles.underline);
                  if (selectionStyles) onUpdateSegmentStyle?.('underline', newVal);
                  else onUpdateStyle('underline', newVal);
                }} icon={Underline} title="Underline" />
                <StyleBtn active={selectionStyles?.strikethrough ?? editStyles.strikethrough} onClick={() => {
                  const newVal = !(selectionStyles?.strikethrough ?? editStyles.strikethrough);
                  if (selectionStyles) onUpdateSegmentStyle?.('strikethrough', newVal);
                  else onUpdateStyle('strikethrough', newVal);
                }} icon={Strikethrough} title="Strikethrough" />

                <div className="w-px h-5 bg-gray-200 mx-1" />

                <div className="flex gap-0.5">
                  {[
                    { id: 'left', icon: AlignLeft, label: 'Align left' },
                    { id: 'center', icon: AlignCenter, label: 'Align center' },
                    { id: 'right', icon: AlignRight, label: 'Align right' },
                  ].map((a) => (
                    <StyleBtn key={a.id} active={editStyles.alignment === a.id} onClick={() => onUpdateStyle('alignment', a.id)} icon={a.icon} title={a.label} />
                  ))}
                </div>

                {/* Line spacing dropdown */}
                <LineSpacingDropdown value={editStyles.lineHeight || 1.3} onChange={(v) => onUpdateStyle('lineHeight', v)} />

                <div className="w-px h-5 bg-gray-200 mx-1" />

                {/* Text color */}
                <ColorPicker
                  value={selectionStyles?.color || editStyles.color || '#000000'}
                  onChange={(hex) => {
                    if (selectionStyles) onUpdateSegmentStyle?.('color', hex);
                    else onUpdateStyle('color', hex);
                  }}
                  label="Text color"
                />

                {/* Background color */}
                <ColorPicker
                  value={editStyles.backgroundColor || 'transparent'}
                  onChange={(hex) => onUpdateStyle('backgroundColor', hex)}
                  label="Background color"
                  allowTransparent
                />
              </>
            )}

            {/* Annotation contextual options — active tool or selected annotation */}
            {(annotActiveTool || selectedAnnotation) && !showTextOptions && annotActiveTool !== 'eraser' && (
              <>
                {showAnnotColors && (
                  <ColorPicker
                    value={selectedAnnotation ? selectedAnnotation.color : annotColor}
                    onChange={(hex) => onSetAnnotColor?.(hex)}
                    label="Color"
                  />
                )}

                {showAnnotStroke && (
                  <NumberSpinner
                    label="Thickness"
                    value={selectedAnnotation ? (selectedAnnotation.strokeWidth || 2) : annotStrokeWidth}
                    min={1}
                    max={50}
                    onChange={(v) => onSetAnnotStrokeWidth?.(v)}
                  />
                )}

                {showAnnotOpacity && (
                  <NumberSpinner
                    label="Opacity"
                    value={Math.round((selectedAnnotation ? selectedAnnotation.opacity : annotOpacity) * 100)}
                    min={10}
                    max={100}
                    step={5}
                    onChange={(v) => onSetAnnotOpacity?.(v / 100)}
                  />
                )}
              </>
            )}

            {/* Eraser contextual options */}
            {showEraserThickness && (
              <NumberSpinner
                label="Thickness"
                value={annotStrokeWidth}
                min={1}
                max={50}
                onChange={(v) => onSetAnnotStrokeWidth?.(v)}
              />
            )}
          </div>
          </div>
        )}
      </div>

      {/* Mobile: floating popover for text formatting (only when a text box is selected) */}
      {showTextOptions && isMobile && hasSelection && (
        <TextFormatPopover
          anchorRect={selectedEditRect}
          editStyles={editStyles}
          onUpdateStyle={onUpdateStyle}
          onClose={() => {}}
        />
      )}
    </>
  );
}

/** Small style toggle button (B, I, U, S, alignment).
 *  Prevents mousedown default so contenteditable keeps focus and text selection. */
function StyleBtn({ active, onClick, icon: Icon, title }) {
  return (
    <button
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={`p-1 rounded transition-colors ${active ? 'bg-brand-blue text-white' : 'text-gray-500 hover:bg-gray-100'}`}
      title={title}
    >
      <Icon size={14} />
    </button>
  );
}

/** Tool button with label (Text, Image, Sign, Date) */
function ToolBtn({ active, onClick, icon: Icon, label, locked, proBadge }) {
  if (locked) {
    return (
      <ProLock feature="edit-mode">
        <button
          className="flex items-center justify-center gap-1 px-2 py-1.5 rounded-md text-sm text-gray-300 cursor-not-allowed min-h-[44px] sm:min-h-0"
          title={label}
        >
          <Icon size={15} />
          <span className="text-xs hidden sm:inline">{label}</span>
          {proBadge && (
            <span
              className="inline-flex items-center px-1 py-px rounded text-[8px] font-bold uppercase tracking-wider text-white leading-none"
              style={{ background: 'linear-gradient(135deg, #F97066, #FB923C)' }}
            >
              Pro
            </span>
          )}
        </button>
      </ProLock>
    );
  }
  return (
    <button
      onClick={onClick}
      className={`flex items-center justify-center gap-1 px-2 py-1.5 rounded-md text-sm transition-colors min-h-[44px] sm:min-h-0 ${
        active ? 'bg-brand-blue text-white' : 'text-gray-600 hover:bg-gray-100'
      }`}
      title={label}
    >
      <Icon size={15} />
      <span className="text-xs hidden sm:inline">{label}</span>
      {proBadge && !active && (
        <span
          className="inline-flex items-center px-1 py-px rounded text-[8px] font-bold uppercase tracking-wider text-white leading-none"
          style={{ background: 'linear-gradient(135deg, #F97066, #FB923C)' }}
        >
          Pro
        </span>
      )}
    </button>
  );
}

/** Number spinner with up/down buttons (like Smallpdf's thickness/opacity controls) */
function NumberSpinner({ label, value, min = 1, max = 100, step = 1, onChange }) {
  return (
    <div className="flex items-center gap-1" title={label}>
      <span className="text-[10px] text-gray-500">{label}</span>
      <div className="flex items-center border border-gray-300 rounded h-6">
        <input
          type="number"
          value={value}
          min={min}
          max={max}
          step={step}
          onChange={(e) => {
            const v = parseInt(e.target.value, 10);
            if (!isNaN(v) && v >= min && v <= max) onChange(v);
          }}
          className="w-10 h-full text-center text-xs border-none outline-none bg-transparent [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
        />
        <div className="flex flex-col border-l border-gray-300">
          <button
            onClick={() => onChange(Math.min(max, value + step))}
            className="px-1 h-3 flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-50"
          >
            <ChevronDown size={8} className="rotate-180" />
          </button>
          <button
            onClick={() => onChange(Math.max(min, value - step))}
            className="px-1 h-3 flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-50 border-t border-gray-200"
          >
            <ChevronDown size={8} />
          </button>
        </div>
      </div>
    </div>
  );
}

/** Line spacing dropdown */
function LineSpacingDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener('mousedown', handleClick);
    return () => window.removeEventListener('mousedown', handleClick);
  }, [open]);

  const currentLabel = LINE_HEIGHTS.find((lh) => lh.value === value)?.label || `${value}`;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className={`p-1 rounded transition-colors ${open ? 'bg-brand-blue text-white' : 'text-gray-500 hover:bg-gray-100'}`}
        title={`Line spacing: ${currentLabel}`}
      >
        <AlignJustify size={14} />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50 min-w-[120px]">
          {LINE_HEIGHTS.map((lh) => (
            <button
              key={lh.value}
              onClick={() => { onChange(lh.value); setOpen(false); }}
              className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-50 transition-colors ${
                value === lh.value ? 'text-brand-blue font-medium' : 'text-gray-700'
              }`}
            >
              {lh.label} <span className="text-gray-400 text-xs ml-auto">{lh.value}x</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Custom dropdown that uses a button trigger (no native <select>), so it doesn't steal focus from contenteditable. */
function ButtonDropdown({ value, options, onChange, renderOption, className, title }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener('mousedown', handleClick);
    return () => window.removeEventListener('mousedown', handleClick);
  }, [open]);

  const selectedOption = options.find((o) => o.value === value);
  const displayLabel = selectedOption?.label || String(value);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={(e) => { e.preventDefault(); setOpen((v) => !v); }}
        onMouseDown={(e) => e.preventDefault()}
        className={`flex items-center justify-between gap-1 px-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:border-brand-blue ${className || ''}`}
        title={title}
        role="listbox"
      >
        <span className="truncate">{displayLabel}</span>
        <ChevronDown size={10} className="shrink-0 text-gray-400" />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50 min-w-full max-h-48 overflow-y-auto">
          {options.map((opt) => (
            <button
              key={opt.label}
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => { e.preventDefault(); onChange(opt.value); setOpen(false); }}
              className={`w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 transition-colors ${
                value === opt.value ? 'text-brand-blue font-medium' : 'text-gray-700'
              }`}
            >
              {renderOption ? renderOption(opt) : opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Dropdown group button (Pen, Shapes) — always opens dropdown on click */
function DropdownGroup({ label, tools, activeTool, isGroupActive, open, onToggle, onSelect, locked }) {
  const activeSubTool = tools.find((t) => t.id === activeTool);
  const displayTool = activeSubTool || tools[0];
  const DisplayIcon = displayTool.icon;

  const groupBtn = (
    <div className="relative inline-flex">
      <button
        onClick={() => !locked && onToggle()}
        disabled={locked}
        className={`flex items-center gap-0.5 px-2 py-1.5 rounded-md text-sm transition-colors ${
          isGroupActive ? 'bg-brand-blue text-white' : locked ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-100'
        }`}
        title={locked ? `${label} (Pro)` : label}
      >
        <DisplayIcon size={15} />
        <ChevronDown size={10} />
      </button>

      {/* Dropdown menu */}
      {open && !locked && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50 min-w-[140px]">
          {tools.map((tool) => (
            <button
              key={tool.id}
              onClick={() => onSelect(tool.id)}
              className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-50 transition-colors ${
                activeTool === tool.id ? 'text-brand-blue font-medium' : 'text-gray-700'
              }`}
            >
              <tool.icon size={14} />
              {tool.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  if (locked) {
    return (
      <ProLock feature="annotations-full" className="inline-flex">
        {groupBtn}
      </ProLock>
    );
  }

  return groupBtn;
}
